import { getStoredValue, type PaymentDetails, getTokenizedCreditCard } from '@koala/sdk/v4';
import { call, put, type SagaReturnType, select, throttle, takeLatest } from 'redux-saga/effects';
import { selectBasketSlice } from '../basket';
import orderStatusActions from '../orderStatus/actions';
import actions from './actions';
import { storedValueErrors } from './constants';
import { ERROR_MESSAGES } from '@/constants/events';
import { createHttpClient } from '@/services/client';
import { type RootState } from '@/types/app';
import { getOrigin } from '@/utils';
import { prepareErrorMessage } from '@/utils/global';
import * as ErrorReporter from '@/utils/errorReporter';

/**
 * Payment type toggle
 *
 */
function* paymentTypeToggleSaga() {
  try {
    const state: RootState = yield select();
    const { checkoutBasket } = state.app.basket;
    // If toggling payment types for a brand that does not support split payment,
    // ensure any applied gift cards are cleared
    if (!checkoutBasket?.location.supports_gift_cards) {
      yield put(actions.storedValueClear());
    }
  } catch (error) {
    ErrorReporter.captureException(error, 'error', {
      eventName: ERROR_MESSAGES.PAYMENT_TOGGLE_SAGA_ERROR,
    });
  }
}

/**
 * Exchange transaction id for card aliases
 *
 */
export function* creditCardTransactionIdSaga(
  action: ReturnType<typeof actions.creditCardTransactionIdGet>,
) {
  try {
    const origin = getOrigin(window.location.host);
    const client = createHttpClient({ origin });
    // Grab the Basket & Location IDs from the store.
    const { checkoutBasket }: ReturnType<typeof selectBasketSlice> =
      yield select(selectBasketSlice);

    // Exchange the Transaction ID for tokenized card details.
    // NOTE: This is a POST (mutation) — do NOT retry. See COO_API_FAILURE_HANDLING.md.
    const res: SagaReturnType<typeof getTokenizedCreditCard> = yield call(
      getTokenizedCreditCard,
      {
        basketId: checkoutBasket.id,
        locationId: checkoutBasket.location.id,
        transactionId: action.creditCard.id!,
      },
      { client },
    );

    // Store the full tokenized card details.
    const creditCardWithAlias: PaymentDetails = {
      ...action.creditCard,
      alias_cc: res.alias_cc,
      alias_cvv: res.alias_cvv,
      country: res.country,
      last_four: res.last_four,
      masked_card: res.masked_card,
    };
    yield put(actions.creditCardSet(creditCardWithAlias));
  } catch (error) {
    /**
     * If exchanging the Transaction ID for the tokenized card fails, store
     * the untokenized card and the Ordering API will re-attempt the exchange.
     */
    yield put(actions.creditCardSet(action.creditCard));
  }
}

/**
 * Get stored value
 *
 */
export function* storeValuePostSaga(action: ReturnType<typeof orderStatusActions.storedValuePost>) {
  try {
    const state: RootState = yield select();
    const { checkoutBasket } = state.app.basket;
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });
    const response: SagaReturnType<typeof getStoredValue> = yield call(
      getStoredValue,
      {
        basketId: checkoutBasket?.id,
        locationId: checkoutBasket?.location.id,
        cardNumber: action.cardToken,
        pin: action.pin,
        captcha: action.captcha,
      },
      { client },
    );

    // Send response to payment saga for calculation/processing
    yield put(
      actions.storedValueSet({
        balance: response.balance,
        amount_authorized: response.amount_authorized,
        remaining_balance: response.remaining_balance,
        card_number: action.cardNumber,
      }),
    );

    // Release STORED_VALUE_POSTING (a "mutating" state — type UPDATING) BEFORE re-validating,
    // so basketMutationGuard lets VALIDATE_ORDER through instead of deferring it until its 30s
    // stuck-release timer (which would freeze checkout for 30s on every gift-card apply). Both
    // puts are synchronous, so VALIDATING_ORDER lands before any render — no mid-refresh submit
    // window opens. Mirrors tipAddSaga (redux/orderStatus/saga.ts).
    yield put(orderStatusActions.orderPending());
    // Re-validate the basket so checkout totals reflect any service-fee waiver. Pass
    // rearmOnFailure (TEAM-14818): validateOrderSaga restores ORDER_PENDING on success or on a
    // non-terminal refresh failure — but NOT on a terminal result (unavailable items, delivery/
    // restaurant unavailable, 502), so the diner can never submit a basket the validate just
    // flagged, and a failed refresh never strands checkout.
    if (checkoutBasket?.id && checkoutBasket?.location?.id) {
      yield put(
        orderStatusActions.validateOrder(checkoutBasket.id, checkoutBasket.location.id, true),
      );
    }
  } catch (error) {
    yield put(orderStatusActions.orderPending());

    // Error Notification
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      null,
      error,
    );

    /**
     * TEAM-13882: this used to display `error_description` verbatim — an unfunneled leak — and the
     * `.indexOf` below ran under an `@ts-expect-error` on a value that is routinely `null`, throwing
     * a TypeError inside this catch so the diner saw no gift-card error at all. Show the resolved
     * copy; keep the captcha special case on the raw detail.
     */
    const errorMessage = errorResponse.detail.includes(
      'We could not validate the authenticity of your request',
    )
      ? storedValueErrors.CAPTCHA
      : errorResponse.message;

    yield put(actions.storedValueError(errorMessage));

    // Fire event
    ErrorReporter.captureException(error, 'error', {
      eventName: ERROR_MESSAGES.POST_STORED_VALUE_ERROR,
      errorResponse,
    });
  }
}

export default function* rootSaga() {
  yield throttle(1500, actions.CREDIT_CARD_TRANSACTION_ID_GET, creditCardTransactionIdSaga);
  yield takeLatest(orderStatusActions.STORED_VALUE_POST, storeValuePostSaga);
  yield takeLatest(actions.BILLING_ACCOUNT_SET, paymentTypeToggleSaga);
  yield takeLatest(actions.PAY_IN_STORE_SET, paymentTypeToggleSaga);
  yield takeLatest(actions.PAYMENT_TYPE_RESET, paymentTypeToggleSaga);
}
